import { useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { useNavigate, useParams } from "react-router-dom";
import UserAuthStore from "../../store/userStore";
import { useGetAIModels, useSpectraDetailsQuery } from "../../api/queryHooks";
import { getInitials } from "../../utils/helpers";
import { EditIcon } from "../../assets";
import { PrimaryButton } from "../../components/Button/Buttons";
import InlineSpinner from "../../components/shared/Spinner/InlineSpinner";
import ModelBadge from "../../components/shared/Model/ModelBadge";
import { Globe, Users, MessageSquare, X, FileText, Send } from "lucide-react";
import Loader from "../../components/Loader/Loader";

import Header from "../../components/Header/Header";
import { PERMISSIONS } from "../../utils/permissions";
import { useAddCollaboratorMutation, useRemoveCollaboratorMutation } from "../../api/queryHooks";
import { isValidEmail } from "../../utils/helpers";
import useSessionIdStore from "../../store/useSessionIdStore";
import { useQueryClient } from "@tanstack/react-query";
import QUERYKEYS from "../../api/queryKeys";
import useFeedback from "../../hooks/useFeedback";
import { AnimatePresence, motion } from "framer-motion";
import RemoveCollaboratorDialog from "../../components/Spectra/RemoveCollaboratorDialog";
import mediaUrlAdapter from "../../api/Media/mediaUrlAdapter";
import logger from "../../services/Logger";

export const FilePreviewReadOnly = ({ file }) => {
  const handleOpen = async () => {
    try {
      const resolvedAsset = await mediaUrlAdapter.resolveAsset(file.asset_id);
      window.open(resolvedAsset.url, "_blank", "noopener,noreferrer");
    } catch (error) {
      logger.error("SpectraLandingPage", "Failed to open attachment", {
        assetId: file.asset_id,
        error: error.message,
      });
    }
  };

  return (
    <button
      type="button"
      onClick={handleOpen}
      className="flex items-start gap-2 px-3 py-2 bg-fill-navigation-hover dark:bg-fill-navigation-surface-dark rounded-xl w-full hover:opacity-80 transition-opacity text-left"
      title={`Open ${file.filename}`}
    >
      <span className="text-xs font-medium text-ink-ink-alternate dark:text-ink-sidebar-text-dark flex-1 min-w-0 line-clamp-2 break-words">
        {file.filename}
      </span>
      <span className="text-xs text-ink-gray-500 flex-shrink-0 mt-[1px]">
        {(file.size_bytes / 1024 / 1024).toFixed(2)} MB
      </span>
    </button>
  );
};

const SpectraLandingPage = () => {
  const { showFeedback } = useFeedback();
  const { space_id } = useParams();
  const navigate = useNavigate();
  const user = UserAuthStore((state) => state.user);
  const orgId = UserAuthStore((state) => state.orgId);
  const queryClient = useQueryClient();

  const [showAllFiles, setShowAllFiles] = useState(false);
  const [showAllSparks, setShowAllSparks] = useState(false);
  const [showFullPrompt, setShowFullPrompt] = useState(false);
  const [isInviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [removingCollaboratorEmail, setRemovingCollaboratorEmail] = useState(null);
  const [collaboratorToRemove, setCollaboratorToRemove] = useState(null);

  // Remove collaborator mutation
  const { mutate: removeCollaborator } = useRemoveCollaboratorMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERYKEYS.SPECTRA_DETAILS] });
      showFeedback("Collaborator removed successfully", "success");
      setRemovingCollaboratorEmail(null);
    },
    onError: (error) => {
      showFeedback("Failed to remove collaborator. Please try again.", "error");
      setRemovingCollaboratorEmail(null);
    },
  });

  // Handler to remove collaborator
  const handleRemoveCollaborator = (collab) => {
    if (removingCollaboratorEmail) return;
    setCollaboratorToRemove(collab);
  };

  const confirmRemoveCollaborator = () => {
    if (!collaboratorToRemove) return;
    setRemovingCollaboratorEmail(collaboratorToRemove.email);
    removeCollaborator({
      sid: space_id,
      uid: spectra.owner_id,
      org_id: orgId,
      user_email: collaboratorToRemove.email,
      session_id: useSessionIdStore.getState().session_id,
    });
    setCollaboratorToRemove(null);
  };

  const cancelRemoveCollaborator = () => {
    setCollaboratorToRemove(null);
  };

  // Fetch comprehensive Spectra data
  // Backend derives owner_id automatically from owned or shared_spaces
  const { data: spectraDetails, isLoading: loading } = useSpectraDetailsQuery(
    space_id,
    orgId,
    user?.uid,
    null, // Let backend derive owner_id
    {
      include_activities: false,
      include_sparks: true,
    }
  );

  const spectra = spectraDetails?.spectra;
  const collaborators = spectraDetails?.collaborators || [];
  const sparks = spectraDetails?.sparks || [];
  const { data: allModels } = useGetAIModels();

  const { mutate: addCollaborator, isPending: isInviting } = useAddCollaboratorMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERYKEYS.SPECTRA_DETAILS] });
    },
    onError: (error) => {
      const backendError = error?.response?.data?.error;
      showFeedback(backendError || "Failed to add collaborator. Please try again.", "error");
    },
  });

  const handleSendInvite = async () => {
    const emails = inviteEmail.split(',').map(e => e.trim()).filter(e => e);
    const validEmails = emails.filter(email => isValidEmail(email));
    
    if (validEmails.length === 0) {
      showFeedback("Please enter at least one valid email address", "error");
      return;
    }

    const existingEmails = collaborators.map(c => c.email?.toLowerCase());
    const newEmails = validEmails.filter(email => !existingEmails.includes(email.toLowerCase()));
    
    if (newEmails.length === 0) {
      showFeedback("All users are already collaborators", "info");
      setInviteEmail("");
      return;
    }

    const results = [];

    for (const email of newEmails) {
      try {
        await new Promise((resolve, reject) => {
          addCollaborator({
            sender_id: user.uid,
            org_id: orgId,
            receiver_email: email,
            space_id: space_id,
            chat_id: null,
            session_id: useSessionIdStore.getState().session_id,
          }, {
            onSuccess: () => {
              results.push({ success: true });
              resolve();
            },
            onError: (error) => {
              results.push({ success: false });
              reject(error);
            }
          });
        });
      } catch (error) {
        // Error already handled in mutation
      }
    }

    const successCount = results.filter(r => r.success).length;
    const failCount = results.filter(r => !r.success).length;

    setInviteEmail("");
    setInviteOpen(false);

    if (successCount > 0 && failCount === 0) {
      showFeedback(`${successCount} invitation${successCount > 1 ? 's' : ''} sent successfully!`, "success");
    } else if (successCount > 0 && failCount > 0) {
      showFeedback(`${successCount} invitation${successCount > 1 ? 's' : ''} sent, ${failCount} failed`, "warning");
    } else if (failCount > 0) {
      showFeedback(`Failed to send invitation${failCount > 1 ? 's' : ''}`, "error");
    }
  };

  const handleInviteKeyPress = (e) => {
    if (e.key === 'Enter') {
      const emails = inviteEmail.split(',').map(e => e.trim()).filter(e => e);
      const hasValidEmail = emails.some(email => isValidEmail(email));
      if (hasValidEmail) {
        handleSendInvite();
      }
    }
  };

  const handleEditClick = () => {
    navigate(`/spectra/${space_id}/edit`);
  };

  const handleCreateSpark = () => {
    navigate(`/spectra/${space_id}/chat/${uuidv4()}`);
  };

  const handleOpenSpark = (sparkId) => {
    navigate(`/spectra/${space_id}/chat/${sparkId}`);
  };

  if (loading) {
    return (
      <div className="w-full">
        <Header type="spectra-dashboard" />
        <div className="h-[calc(100vh-80px)] overflow-y-auto">
          <Loader variant="spectra-landing" />
        </div>
      </div>
    );
  }

  if (!spectra) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-ink-gray-500">Spectra not found</div>
      </div>
    );
  }

  const isOwner = PERMISSIONS.IS_OWNER(spectra);
  const canInvite = PERMISSIONS.CAN_INVITE_COLLABORATORS(spectra);
  const chatIds = spectra.chat_ids || [];
  const resolvedModelName =
    spectra.model_name ||
    allModels?.find(
      (model) => model.id === spectra.model_id || model.model_id === spectra.model_id
    )?.name ||
    spectra.model_id ||
    "No Model Selected";
  const systemPrompt = spectra.system_prompt?.trim() || "";
  const isLongPrompt = systemPrompt.length > 280 || systemPrompt.split("\n").length > 4;
  const webSearchEnabled = spectra.web_search_enabled !== false;
  const files = spectra.attachments || [];
  
  const emails = inviteEmail.split(',').map(e => e.trim()).filter(e => e);
  const hasValidEmail = emails.some(email => isValidEmail(email));
  const showInviteButton = hasValidEmail && !isInviting;
  
  // Filter chatIds to only include sparks that actually exist in the backend response
  // This prevents showing "Untitled Spark" for chats that have been moved or deleted
  const validChatIds = chatIds.filter(chatId => {
    const actualSparkId = typeof chatId === 'object' ? chatId?.chat_id : chatId;
    return sparks.some(s => s.uid === actualSparkId);
  });

  return (
    <div className="w-full">
      <Header type="spectra-dashboard" />
      
      <div className="h-[calc(100vh-80px)] overflow-auto show-scrollbar">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-6">
        {/* Header Section */}
        <div className="bg-surface-panel dark:bg-surface-panel rounded-2xl shadow-sm p-6 mb-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-4 min-w-0">
              <div className="photon-avatar text-lg font-semibold w-16 h-16 flex items-center justify-center rounded-full flex-shrink-0">
                {spectra?.emoji ? (
                  <span className="text-3xl">{spectra.emoji.emoji}</span>
                ) : (
                  <span>{getInitials(spectra.title)?.toUpperCase()}</span>
                )}
              </div>
              <div className="min-w-0">
                <h1 className="text-2xl font-bold text-ink-ink dark:text-ink-white">
                  {spectra.title}
                </h1>
                {spectra.tag && (
                  <span className="text-sm text-ink-gray-500 italic block mt-0.5">{spectra.tag}</span>
                )}
                {spectra.description && (
                  <p className="text-sm text-ink-gray-600 dark:text-ink-gray-400 mt-1">
                    {spectra.description}
                  </p>
                )}
              </div>
            </div>
            {isOwner && (
              <button
                onClick={handleEditClick}
                className="flex items-center gap-2 px-4 py-2 bg-fill-sidebar-action-surface dark:bg-fill-control-border rounded-lg hover:opacity-80 transition-opacity flex-shrink-0"
              >
                <EditIcon className="w-4 h-4 dark:fill-ink-white fill-ink-ink" />
                <span className="text-sm font-medium text-ink-ink dark:text-ink-white">
                  Edit
                </span>
              </button>
            )}
          </div>

          {/* Read-only System Instructions + Model footer (visible to owners and collaborators) */}
          <div className="mt-4 pt-4 border-t border-edge-gray-200 dark:border-edge-gray-700">
            <div className="flex items-center justify-between mb-1.5">
              <h2 className="text-xs font-semibold uppercase tracking-wide text-ink-gray-500 dark:text-ink-gray-400">
                System Instructions
              </h2>
              {isLongPrompt && (
                <button
                  type="button"
                  onClick={() => setShowFullPrompt((prev) => !prev)}
                  className="text-xs text-ink-blue-600 dark:text-ink-blue-400 hover:underline"
                >
                  {showFullPrompt ? "Show less" : "Show more"}
                </button>
              )}
            </div>
            {systemPrompt ? (
              <p
                data-testid="spectra-system-prompt"
                role={isLongPrompt ? "button" : undefined}
                tabIndex={isLongPrompt ? 0 : undefined}
                aria-expanded={isLongPrompt ? showFullPrompt : undefined}
                onClick={
                  isLongPrompt
                    ? () => {
                        if (window.getSelection?.()?.toString()) return;
                        setShowFullPrompt((prev) => !prev);
                      }
                    : undefined
                }
                onKeyDown={
                  isLongPrompt
                    ? (e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setShowFullPrompt((prev) => !prev);
                        }
                      }
                    : undefined
                }
                className={`text-sm text-ink-ink dark:text-ink-white whitespace-pre-wrap break-words bg-surface-muted-panel dark:bg-surface-muted-panel rounded-xl p-3 ${
                  isLongPrompt ? "cursor-pointer hover:opacity-90 transition-opacity" : ""
                } ${!showFullPrompt && isLongPrompt ? "line-clamp-4" : ""}`}
              >
                {systemPrompt}
              </p>
            ) : (
              <p
                data-testid="spectra-system-prompt-empty"
                className="text-sm text-ink-gray-500 italic"
              >
                No system instructions provided.
              </p>
            )}
            <div data-testid="spectra-model-badge" className="flex items-center justify-end gap-2 mt-2">
              <span
                data-testid="spectra-web-search-indicator"
                title={webSearchEnabled ? "Web search enabled" : "Web search disabled"}
                aria-label={webSearchEnabled ? "Web search enabled" : "Web search disabled"}
                className="inline-flex items-center"
              >
                <Globe
                  size={13}
                  className={
                    webSearchEnabled
                      ? "text-ink-primary dark:text-ink-accent"
                      : "text-ink-control-border dark:text-ink-gray-400 opacity-60"
                  }
                />
              </span>
              <ModelBadge
                model_id={spectra.model_id || resolvedModelName}
                modelDbName={resolvedModelName}
              />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Sparks Section */}
          <div className="bg-surface-panel dark:bg-surface-panel rounded-2xl shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-ink-ink dark:text-ink-white" />
                <h2 className="text-lg font-semibold text-ink-ink dark:text-ink-white">
                  Sparks ({validChatIds.length})
                </h2>
              </div>
              {isOwner && (
                <button
                  onClick={handleCreateSpark}
                  className="text-sm text-ink-blue-600 dark:text-ink-blue-400 hover:underline"
                >
                  + Spark
                </button>
              )}
            </div>
            <div className="space-y-2">
              {validChatIds.length > 0 ? (
                (showAllSparks ? validChatIds : validChatIds.slice(0, 5)).map((sparkId, index) => {
                  const actualSparkId = typeof sparkId === 'object' ? sparkId?.chat_id : sparkId;
                  const spark = sparks?.find(s => s.uid === actualSparkId);
                  const title = spark?.title || 'Untitled Spark';
                  return (
                    <div
                      key={`spark-${index}-${actualSparkId || ''}`}
                      onClick={() => handleOpenSpark(actualSparkId)}
                      className="flex items-center justify-between p-3 bg-surface-muted-panel dark:bg-surface-muted-panel rounded-xl cursor-pointer hover:opacity-80 transition-opacity"
                    >
                      <div className="flex items-center gap-2">
                        <MessageSquare className="w-4 h-4 text-ink-gray-500" />
                        <span className="text-sm text-ink-ink dark:text-ink-white truncate">
                          {title}
                        </span>
                      </div>
                      <span className="text-xs text-ink-gray-500">Open →</span>
                    </div>
                  );
                })
              ) : (
                <p className="text-sm text-ink-gray-500 text-center py-4">
                  No sparks yet. Create one to start collaborating!
                </p>
              )}
              {validChatIds.length > 5 && (
                <button 
                  onClick={() => setShowAllSparks(!showAllSparks)}
                  className="w-full text-sm text-ink-blue-600 dark:text-ink-blue-400 hover:underline pt-2"
                >
                  {showAllSparks ? "Show less" : `View all ${validChatIds.length} sparks →`}
                </button>
              )}
            </div>
          </div>

          {/* Files Section */}
          <div className="bg-surface-panel dark:bg-surface-panel rounded-2xl shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-ink-ink dark:text-ink-white" />
                <h2 className="text-lg font-semibold text-ink-ink dark:text-ink-white">
                  Files ({files.length})
                </h2>
              </div>
            </div>
            {files.length > 0 ? (
              <div className="space-y-3">
                <div className="flex flex-col gap-2">
                  {(showAllFiles ? files : files.slice(0, 5)).map((file, index) => (
                    <FilePreviewReadOnly key={file.asset_id || index} file={file} />
                  ))}
                </div>
                {files.length > 5 && (
                  <button 
                    onClick={() => setShowAllFiles(!showAllFiles)}
                    className="w-full text-sm text-ink-blue-600 dark:text-ink-blue-400 hover:underline text-center pt-2"
                  >
                    {showAllFiles ? "Show less" : `View all ${files.length} files →`}
                  </button>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-center p-6 bg-surface-muted-panel dark:bg-surface-muted-panel rounded-xl">
                <span className="text-sm text-ink-gray-500">
                  No files uploaded yet
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Collaborators Section */}
        <div className="bg-surface-panel dark:bg-surface-panel rounded-2xl shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-ink-ink dark:text-ink-white" />
              <h2 className="text-lg font-semibold text-ink-ink dark:text-ink-white">
                Collaborators ({collaborators.length})
              </h2>
            </div>
            {canInvite && (
              <PrimaryButton
                onClick={() => setInviteOpen(!isInviteOpen)}
                className="flex items-center gap-2"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8 3V13M3 8H13" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                Share
              </PrimaryButton>
            )}
          </div>
          
          <AnimatePresence>
            {isInviteOpen && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden mb-4"
              >
                <div className="bg-surface-muted-panel dark:bg-surface-muted-panel rounded-lg p-4">
                  <div className="text-ink-ink dark:text-ink-white text-sm font-semibold mb-3">
                    Invite Contributor
                  </div>
                  <div className="relative max-w-md">
                    <input
                      type="email"
                      placeholder="email@example.com, another@example.com"
                      className="photon-field w-full pr-12 pl-4 py-2.5 border border-edge-hex-cccccc dark:border-edge-muted-control rounded-lg bg-fill-white dark:bg-fill-dialog-surface text-ink-ink dark:text-ink-white text-sm focus:outline-none focus:border-edge-gradient-primary"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      onKeyPress={handleInviteKeyPress}
                      autoFocus
                    />
                    <button
                      onClick={handleSendInvite}
                      disabled={!showInviteButton}
                      className={`photon-primary-action bg-button-primary text-button-on-primary absolute top-1/2 right-2 -translate-y-1/2 w-8 h-8 rounded-full flex items-center justify-center transition-colors duration-200 ${
                        showInviteButton
                          ? "opacity-100 cursor-pointer"
                          : "opacity-50 cursor-not-allowed"
                      }`}
                    >
                      <Send className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {collaborators.map((collab, index) => {
              const isPending = collab.status === "PENDING";
              const isCollabOwner = collab.user_id === spectra.owner_id;
              // Use enriched user data if available, otherwise fallback
              const userData = collab.user || {};
              const displayName = userData.name || collab.email || "Unknown User";
              const email = userData.email || collab.email;
              
              return (
                <div
                  key={email || collab.user_id || index}
                  className="flex items-center gap-3 p-3 bg-surface-muted-panel dark:bg-surface-muted-panel rounded-xl group"
                >
                  <div className="photon-avatar w-10 h-10 rounded-full flex items-center justify-center">
                    <span className="text-sm font-medium">
                      {getInitials(displayName)}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-ink-ink dark:text-ink-white truncate">
                        {displayName}
                      </span>
                      {isCollabOwner && (
                        <span className="text-xs bg-fill-navigation-hover text-ink-primary px-2 py-0.5 rounded">
                          Owner
                        </span>
                      )}
                    </div>
                    {email && (
                      <span className="text-xs text-ink-gray-500 truncate block">
                        {email}
                      </span>
                    )}
                  </div>
                  {/* Invited badge or Remove button for owner only, not for owner themselves */}
                  {isOwner && !isCollabOwner && (
                    <div className="relative flex items-center justify-center min-w-[60px]">
                      {isPending && (
                        <span className="absolute text-xs bg-fill-hex-fef9c3 dark:bg-fill-yellow-900 text-ink-yellow-800 dark:text-ink-hex-fde047 px-2 py-0.5 rounded group-hover:opacity-0 transition-opacity">
                          Invited
                        </span>
                      )}
                      <button
                        onClick={() => handleRemoveCollaborator(collab)}
                        disabled={removingCollaboratorEmail === collab.email}
                        className="absolute opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-fill-red-100 dark:hover:bg-fill-red-900/30 rounded disabled:opacity-50"
                        title={isPending ? "Cancel invitation" : "Remove collaborator"}
                      >
                        {removingCollaboratorEmail === collab.email ? (
                          <InlineSpinner className="text-ink-red-600 dark:text-ink-red-400" />
                        ) : (
                          <X className="w-4 h-4 text-ink-red-600 dark:text-ink-red-400" />
                        )}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        </div>
      </div>

      {/* Remove Collaborator Confirmation Dialog */}
      <RemoveCollaboratorDialog
        collaborator={collaboratorToRemove}
        isRemoving={removingCollaboratorEmail === collaboratorToRemove?.email}
        onConfirm={confirmRemoveCollaborator}
        onCancel={cancelRemoveCollaborator}
      />
    </div>
  );
};

export default SpectraLandingPage;
